// Client-side export dei template statici del Social Studio.
//
// Strategia: il componente StaticTemplatePreview viene già renderizzato con
// dimensioni "vive" (max-w-[420px] per i quadrati, 260px per le story,
// e 4 colonne A4 per la brochure). Per ottenere un PNG ad alta risoluzione,
// usiamo html2canvas con scale che scala il DOM esistente fino al target.
//
// FORMATI E DIMENSIONI TARGET:
//   • post 1:1 (nuovo_annuncio, prezzo_aggiornato, venduto)  → 1080 × 1080 PNG
//   • story 9:16 (open_house)                                 → 1080 × 1920 PNG
//   • brochure A4 (4 pagine)                                  → 2480 × 3508 PDF
//
// I font Google (DM Serif, Bodoni, Archivo, Space Grotesk, Playfair) sono già
// caricati globalmente da index.css, quindi html2canvas li trova nel <head>.

import html2canvas from "html2canvas";
import jsPDF from "jspdf";

/**
 * Cattura un nodo DOM come immagine ad alta risoluzione.
 * @param {HTMLElement} node - Il nodo da renderizzare.
 * @param {number} targetWidth - Larghezza desiderata in pixel finali.
 * @param {string} format - "image/png" o "image/jpeg".
 * @param {number} quality - Per JPEG, 0-1.
 * @returns {Promise<Blob>}
 */
async function captureAsImage(node, targetWidth, format = "image/png", quality = 1.0) {
  if (!node) throw new Error("captureAsImage: node mancante");
  // iter 312 r4 — aspetta che TUTTI i font siano caricati prima della cattura.
  // Senza questo, html2canvas può catturare quando i Google Fonts (DM Serif,
  // Bodoni, Archivo...) sono ancora "in caricamento" e cade su fallback
  // sans-serif → anteprima e download appaiono diversi (testi più stretti
  // e disposti su numero di righe diverso).
  try { if (document.fonts && document.fonts.ready) await document.fonts.ready; } catch (_) {}
  await waitForBackgroundImages(node);
  // iter v28 r369 — User M1279 (bug): nel volantino scaricato il logo
  // agenzia appare a colori anche se in anteprima è B/W. Root cause:
  // html2canvas non rispetta in modo affidabile il CSS `filter: brightness(0)`
  // sugli `<img>` durante la cattura.
  // Fix: prima della cattura, ricolora in-place i pixel di TUTTI gli
  // <img> taggati con `[data-bw-export]` usando un canvas separato e
  // mettendoli a nero puro (alpha preservato). Dopo il fix, html2canvas
  // cattura un'immagine già B/W e il filter CSS diventa inutile.
  await recolorBlackAndWhiteImages(node);
  // iter v29 r170 — User M1503: «foto profilo sgranata nel volantino».
  // Upscale tutte le <img data-hires-export="N"> a N pixel sul lato lungo
  // PRIMA che html2canvas le rasterizzi. Il browser usa interpolazione
  // bicubica nativa (ben superiore a quella di html2canvas).
  await upscaleHiresImages(node);
  const rect = node.getBoundingClientRect();
  const scale = Math.max(1, targetWidth / Math.max(rect.width, 1));
  const canvas = await html2canvas(node, {
    scale,
    useCORS: true,
    allowTaint: true,
    backgroundColor: format === "image/jpeg" ? "#FFFFFF" : null,
    logging: false,
    imageTimeout: 15000,
  });
  return new Promise((resolve, reject) => {
    canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error("toBlob fallito"))), format, quality);
  });
}

/**
 * iter v28 r369 — Pre-processa gli `<img>` taggati `data-bw-export`:
 * ridisegna ogni pixel in nero puro (RGB 0,0,0) mantenendo l'alpha originale.
 * Risultato: logo agenzia diventa una silhouette nera con la stessa forma.
 *
 * iter v28 r433 — User M1345: «il logo è rovinato anche a 300 DPI».
 * Il logo source dell'agente è spesso di bassa risoluzione (es. 200×80px)
 * → quando il PDF viene esportato a 1748px width (300 DPI A5), il logo
 * appare scalato e sfocato. Soluzione: durante il recolor, UPSCALE il
 * canvas a una dimensione di destinazione (default 1024px sul lato lungo)
 * via `drawImage` con `imageSmoothingQuality: high` (bicubic browser
 * native). Il browser interpola in modo molto migliore di html2canvas
 * che si limita a nearest-neighbor durante lo scale. Il logo finale nel
 * PDF risulta crisp e leggibile.
 *
 * Funziona solo se il server del logo serve CORS (Access-Control-Allow-Origin).
 * In caso contrario il canvas viene "tainted" e getImageData lancia
 * SecurityError → l'immagine resta a colori (fallback grazia).
 */
async function recolorBlackAndWhiteImages(root) {
  const imgs = Array.from(root.querySelectorAll('img[data-bw-export]'));
  if (imgs.length === 0) return;
  const tasks = imgs.map((imgEl) => new Promise((resolve) => {
    try {
      const tag = imgEl.getAttribute("data-bw-export");
      const targetWhite = tag === "white";
      const helper = new Image();
      helper.crossOrigin = "anonymous";
      helper.onload = () => {
        try {
          const w = helper.naturalWidth || helper.width;
          const h = helper.naturalHeight || helper.height;
          if (!w || !h) return resolve();
          // iter v28 r433 — upscale a min 1024px sul lato lungo per export
          // print-quality. Se l'immagine è già abbastanza grande non scaliamo.
          const MIN_LONG_EDGE = 1024;
          const longEdge = Math.max(w, h);
          const upscale = Math.max(1, MIN_LONG_EDGE / longEdge);
          const cw = Math.round(w * upscale);
          const ch = Math.round(h * upscale);
          const canvas = document.createElement("canvas");
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(helper, 0, 0, cw, ch);
          const data = ctx.getImageData(0, 0, cw, ch);
          const px = data.data;
          const v = targetWhite ? 255 : 0;
          for (let i = 0; i < px.length; i += 4) {
            if (px[i + 3] > 0) {
              px[i] = v;
              px[i + 1] = v;
              px[i + 2] = v;
            }
          }
          ctx.putImageData(data, 0, 0);
          imgEl.src = canvas.toDataURL("image/png");
          imgEl.style.filter = "none";
        } catch (e) {
          console.warn("[bw-export] recolor skipped (CORS or canvas error):", e?.message || e);
        }
        resolve();
      };
      helper.onerror = () => resolve();
      helper.src = imgEl.src;
    } catch {
      resolve();
    }
  }));
  await Promise.all(tasks);
}

async function captureAsPng(node, targetWidth) {
  return captureAsImage(node, targetWidth, "image/png", 1.0);
}

/**
 * iter v29 r170 — Pre-processa le <img data-hires-export="N"> upscalando
 * la sorgente a N pixel sul lato lungo con interpolazione bicubica
 * browser-nativa, prima che html2canvas la rasterizzi.
 *
 * Caso d'uso: foto profilo agente nel volantino stima. La foto è caricata
 * dall'agente a risoluzione variabile (a volte 200×200, a volte 2000×2000)
 * e viene mostrata in un cerchio piccolo (78×78 CSS). Quando il volantino
 * viene esportato a 1748px width (300 DPI A5), il cerchio diventa ~340×340
 * pixel. html2canvas scala l'<img> originale ma con algoritmo
 * nearest-neighbor → viso sgranato.
 * Soluzione: pre-renderizziamo l'<img> su un canvas alle dimensioni target,
 * sostituiamo il src con il dataURL. Da quel momento html2canvas trova
 * un <img> già a risoluzione perfetta.
 */
async function upscaleHiresImages(root) {
  const imgs = Array.from(root.querySelectorAll('img[data-hires-export]'));
  if (imgs.length === 0) return;
  const tasks = imgs.map((imgEl) => new Promise((resolve) => {
    try {
      const target = parseInt(imgEl.getAttribute("data-hires-export"), 10) || 800;
      const helper = new Image();
      helper.crossOrigin = "anonymous";
      helper.onload = () => {
        try {
          const w = helper.naturalWidth || helper.width;
          const h = helper.naturalHeight || helper.height;
          if (!w || !h) return resolve();
          const longEdge = Math.max(w, h);
          // Se l'immagine è già più grande del target, lasciala (browser
          // farà downscale di alta qualità durante il render).
          if (longEdge >= target) return resolve();
          const scale = target / longEdge;
          const cw = Math.round(w * scale);
          const ch = Math.round(h * scale);
          const canvas = document.createElement("canvas");
          canvas.width = cw;
          canvas.height = ch;
          const ctx = canvas.getContext("2d");
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = "high";
          ctx.drawImage(helper, 0, 0, cw, ch);
          imgEl.src = canvas.toDataURL("image/png");
        } catch (e) {
          // CORS o canvas error — l'immagine resta a risoluzione originale
          console.warn("[hires-export] upscale skipped:", e?.message || e); // eslint-disable-line no-console
        }
        resolve();
      };
      helper.onerror = () => resolve();
      helper.src = imgEl.src;
    } catch {
      resolve();
    }
  }));
  await Promise.all(tasks);
}

/**
 * Aspetta che tutte le `<img>` reali e tutte le background-image nel
 * sottoalbero siano completamente caricate prima della cattura.
 * Critico per il carosello brochure-social: senza questo, le 4 PNG
 * generate sono "vuote" perché html2canvas cattura prima che le immagini
 * della cover/gallery siano scaricate (cross-origin + lazy loading).
 */
async function waitForBackgroundImages(root, timeout = 8000) {
  const tasks = [];

  // 1) <img> reali: aspetta complete + decode quando possibile
  const imgs = root.querySelectorAll("img");
  imgs.forEach((img) => {
    if (img.complete && img.naturalWidth > 0) return;
    tasks.push(
      new Promise((resolve) => {
        const done = () => resolve();
        img.addEventListener("load", done, { once: true });
        img.addEventListener("error", done, { once: true });
        // Force-eager: alcuni img hanno loading="lazy", disabilitalo
        try { img.loading = "eager"; } catch (_) {}
        // safety net
        setTimeout(resolve, timeout);
      })
    );
  });

  // 2) background-image url(...)
  const urls = new Set();
  root.querySelectorAll("*").forEach((el) => {
    const bg = window.getComputedStyle(el).backgroundImage;
    if (bg && bg.startsWith("url(")) {
      const m = bg.match(/url\(["']?([^"')]+)["']?\)/);
      if (m && m[1]) urls.add(m[1]);
    }
  });
  urls.forEach((url) => {
    tasks.push(
      new Promise((resolve) => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => resolve();
        img.onerror = () => resolve();
        img.src = url;
        setTimeout(resolve, timeout);
      })
    );
  });

  if (tasks.length === 0) return;
  await Promise.all(tasks);
  // un microtick extra per dar tempo al browser di "paint" le immagini caricate
  await new Promise((r) => setTimeout(r, 100));
}

/**
 * Esporta un singolo formato come PNG. Il chiamante passa il nodo DOM
 * e la dimensione target.
 *
 * @param {Object} opts
 * @param {HTMLElement} opts.node
 * @param {number} opts.targetWidth
 * @param {string} opts.filename
 * @param {boolean} [opts.skipDownload=false] — iter v28 r187: se true, non
 *   triggera il download (utile per pre-warming/cache: ritorna solo il blob).
 */
export async function exportStaticAsPng({ node, targetWidth, filename, skipDownload = false }) {
  const blob = await captureAsPng(node, targetWidth);
  if (skipDownload) return blob;
  // iter 313 r16 — su iOS Safari `triggerDownload` (anchor click) NON funziona:
  // il file finisce in un blob URL aperto in Safari, ma niente download. Smart
  // download usa Web Share API (allega in File/WhatsApp/Mail) o fallback open-in-tab.
  if (isIOSDevice()) {
    await smartDownloadBlob(blob, filename, "image/png");
  } else {
    triggerDownload(blob, filename);
  }
  return blob;
}

/**
 * Esporta la brochure come PDF di 4 pagine A4.
 * @param {HTMLElement[]} pageNodes - 4 nodi DOM (cover, descrizione, galleria, contatti)
 * @param {string} filename - es. "brochure-prop123.pdf"
 * @param {boolean} [autoSave=true] - Se true scarica il file via pdf.save(). Se false ritorna solo il blob (caller gestisce iOS/Android).
 */
export async function exportBrochureAsPdf({ pageNodes, filename, autoSave = true }) {
  if (!pageNodes || pageNodes.length !== 4) {
    throw new Error("exportBrochureAsPdf richiede esattamente 4 nodi pagina");
  }
  // iter 312 r10 — alzato ulteriormente: 3508px (A4 a ~424 DPI) + JPEG 0.98
  // perché l'utente segnalava ancora pixelazione su prezzo/testi piccoli.
  // I JPEG compressi su testi piccoli con forte contrasto (es. €890.000
  // bianco su accent verde) generano artefatti visibili. A risoluzione
  // maggiore l'artefatto diventa invisibile a occhio.
  const TARGET_WIDTH_HIDPI = 3508;
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  for (let i = 0; i < pageNodes.length; i++) {
    const node = pageNodes[i];
    const blob = await captureAsImage(node, TARGET_WIDTH_HIDPI, "image/jpeg", 0.98);
    const dataUrl = await blobToDataUrl(blob);
    if (i > 0) pdf.addPage();
    // Riempi tutta la pagina A4 (fit)
    pdf.addImage(dataUrl, "JPEG", 0, 0, 210, 297, undefined, "SLOW");
  }
  if (autoSave) pdf.save(filename);
  // iter 246: ritorna anche il blob per archiviazione (Social Assets) + iOS share.
  return pdf.output("blob");
}

/**
 * Esporta un singolo nodo DOM come PDF A4 1 pagina.
 * Usato per il report Stima 1-pagina.
 */
export async function exportSinglePageAsPdf({ node, filename }) {
  if (!node) throw new Error("exportSinglePageAsPdf: node mancante");
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a4", compress: true });
  // iter 312 r10 — A4 a ~424 DPI
  const blob = await captureAsImage(node, 3508, "image/jpeg", 0.98);
  const dataUrl = await blobToDataUrl(blob);
  pdf.addImage(dataUrl, "JPEG", 0, 0, 210, 297, undefined, "SLOW");
  pdf.save(filename);
}

/**
 * Esporta un set di nodi DOM come ZIP di PNG numerate.
 * Usato dal carosello "Brochure social": 4 pagine A4 inquadrate in 4:5
 * Instagram (1080×1350) zippate per upload diretto come carosello IG.
 *
 * @param {HTMLElement[]} nodes - i nodi DOM da catturare (in ordine)
 * @param {number} targetWidth - larghezza output in pixel (default 1080)
 * @param {string} filename - nome del file ZIP
 */
export async function exportNodesAsZip({ nodes, targetWidth = 1080, filename, prefix = "slide" }) {
  if (!nodes || nodes.length === 0) {
    throw new Error("exportNodesAsZip: nessun nodo da esportare");
  }
  const { default: JSZip } = await import("jszip");
  const zip = new JSZip();
  for (let i = 0; i < nodes.length; i += 1) {
    const blob = await captureAsImage(nodes[i], targetWidth, "image/png", 1.0);
    zip.file(`${prefix}-${String(i + 1).padStart(2, "0")}.png`, blob);
  }
  const zipBlob = await zip.generateAsync({ type: "blob" });
  triggerDownload(zipBlob, filename);
  return zipBlob;
}

function blobToDataUrl(blob) {
  return new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onloadend = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(blob);
  });
}

function triggerDownload(blob, filename) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

/**
 * Detect iOS Safari/WebView. Su iOS Safari l'attributo `<a download>` viene
 * ignorato e i PDF generati con `pdf.save()` non finiscono nei Download
 * tradizionali: l'utente non li trova. Per evitarlo, su iOS apriamo il PDF
 * in una nuova tab — Safari mostra il visualizzatore PDF con i bottoni
 * "Condividi" e "Salva in File" già visibili.
 */
export function isIOSDevice() {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  // iPadOS 13+ si presenta come Mac → controllo touch.
  return /iPad|iPhone|iPod/.test(ua) || (ua.includes("Mac") && (navigator.maxTouchPoints || 0) > 1);
}

/**
 * Download "user-friendly" di un PDF/file:
 *   • Desktop / Android → download classico (file in cartella Download).
 *   • iOS Safari → tenta navigator.share (allega in File / WhatsApp / Mail),
 *                  altrimenti apre il blob in una nuova tab per il preview.
 *
 * Ritorna true se la condivisione/apertura è andata a buon fine, false se
 * l'utente ha annullato lo share-sheet (così il caller può evitare il toast
 * di successo "scaricato" che è bugiardo in quel caso).
 */
export async function smartDownloadBlob(blob, filename, mime = "application/pdf") {
  if (isIOSDevice()) {
    // 1) Web Share API: il modo nativo "Salva su File / Condividi" su iOS.
    //    Questo deve essere chiamato in un gesture user fresco (entro pochi ms
    //    dal click), altrimenti Safari abortisce. Vedi VolantinoStimaModal
    //    che pre-genera il blob e mostra un secondo bottone per il share.
    try {
      const file = new File([blob], filename, { type: mime });
      if (navigator.canShare && navigator.canShare({ files: [file] })) {
        await navigator.share({ files: [file], title: filename });
        return true;
      }
    } catch (err) {
      if (err?.name === "AbortError") return false; // utente ha annullato
      console.warn("share failed, fallback to download:", err);
    }
    // 2) Fallback iOS: anchor download. Su Safari iOS recenti il PDF viene
    //    aperto in una nuova tab con i bottoni nativi "Condividi" / "Salva
    //    in File". Più affidabile di window.open(blob:) che a volte mostra
    //    pagina bianca se il blob URL viene revocato troppo presto.
    triggerDownload(blob, filename);
    return true;
  }
  // Desktop/Android: download classico.
  triggerDownload(blob, filename);
  return true;
}

/**
 * Esporta un set di nodi A5 (148×210mm) come PDF multi-pagina.
 * @param {HTMLElement[]} pageNodes - 1 o più nodi DOM (fronte, retro, …)
 * @param {string} filename - es. "volantino-stima.pdf"
 */
/**
 * Iter v27 r6 — Export PDF A5 a 300 DPI (standard tipografico).
 *
 * Calcoli:
 *   A5 = 148 × 210 mm
 *   300 DPI → 148 * 300 / 25.4 = 1748 px wide × 2480 px high
 *   Qualità JPEG 0.95 — invisibile a occhio nudo, riduce il peso del file.
 *
 * 300 DPI è lo standard tipografico per stampa A5 a casa o in copisteria.
 * Era 600 DPI (3496 px) ma su mobile/desktop lento html2canvas impiegava
 * 30-60s e l'utente pensava si fosse "impallato". Con 300 DPI il render
 * scende a 8-15s e la qualità di stampa resta professionale.
 *
 * File size finale ~1-2 MB per PDF 2 pagine.
 */
export async function exportA5MultiAsPdf({ pageNodes, filename, autoSave = true, targetWidth = 1748, jpegQuality = 0.98 }) {
  if (!pageNodes || pageNodes.length === 0) {
    throw new Error("exportA5MultiAsPdf richiede almeno 1 nodo pagina");
  }
  // A5 = 148 × 210 mm.
  const pdf = new jsPDF({ orientation: "portrait", unit: "mm", format: "a5", compress: true });
  for (let i = 0; i < pageNodes.length; i++) {
    const node = pageNodes[i];
    // iter v27 r6 — yield al browser tra le pagine così l'UI non si blocca
    // (l'utente vede lo spinner muoversi invece che "Genera PDF" congelato).
    if (i > 0) await new Promise((r) => setTimeout(r, 50));
    const blob = await captureAsImage(node, targetWidth, "image/jpeg", jpegQuality);
    const dataUrl = await blobToDataUrl(blob);
    if (i > 0) pdf.addPage();
    pdf.addImage(dataUrl, "JPEG", 0, 0, 148, 210, undefined, "SLOW");
  }
  const pdfBlob = pdf.output("blob");
  // Backward-compat: se il caller non gestisce il blob (autoSave true),
  // su desktop chiamiamo triggerDownload. Su iOS NON salviamo qui (sarebbe rotto).
  if (autoSave && filename && !isIOSDevice()) {
    triggerDownload(pdfBlob, filename);
  }
  return pdfBlob;
}

/**
 * Mappa formato → larghezza target in pixel per export PNG.
 * Iter v27 r6: volantino_stima a 1748px (300 DPI A5) standard tipografico.
 */
export const EXPORT_WIDTHS = {
  nuovo_annuncio: 1080,    // 1:1 Instagram
  prezzo_aggiornato: 1080, // 1:1 Instagram
  venduto: 1080,           // 1:1 Instagram
  open_house: 1080,        // 9:16 Instagram story
  volantino_stima: 1748,   // A5 300 DPI (148 mm × 300 DPI / 25.4) — standard stampa
  story_stima: 1080,       // 9:16 Instagram story
  story_nuovo: 1080,       // 9:16 Instagram story
  story_venduto: 1080,     // 9:16 Instagram story
};
